import React from 'react';
import { Alert, Box, Chip, Paper, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { Store as StoreIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { Store } from '../../types';
import DataStep from './DataStep';
import RunWorkspace from './RunWorkspace';
import PurchaseOrders from './PurchaseOrders';
import History from './History';

interface Props {
  selectedStore?: Store | null;
}

/**
 * Order Processor: evitalRx uploads → Order Optimizer rules → review → distributor ranking →
 * purchase orders → receipt matching. Every step is persisted (op_* tables) with an action log.
 */
const OrderProcessor: React.FC<Props> = ({ selectedStore }) => {
  const [params, setParams] = useSearchParams();
  // Store always follows the OMS store selector at the top of the screen (no second selector here)
  const storeId: number | null = selectedStore?.id ?? null;
  const storeName = selectedStore?.store_name || '';
  const tab = params.get('tab') || 'workflow';
  const runId = params.get('run') ? Number(params.get('run')) : null;

  const go = (next: { tab?: string; run?: number | null }) => {
    const p = new URLSearchParams(params);
    if (next.tab) p.set('tab', next.tab);
    if (next.run === null) p.delete('run'); else if (next.run !== undefined) p.set('run', String(next.run));
    setParams(p);
  };
  const openRun = (id: number) => go({ tab: 'workflow', run: id });

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5">Order Processor</Typography>
          <Typography variant="body2" color="text.secondary">
            Upload evitalRx reports → optimizer rules → review → distributor ranking → purchase orders → receipts
          </Typography>
        </Box>
        {storeName && (
          <Tooltip title="Uploads and new runs use this store. Change it with the store button at the top of the screen.">
            <Chip icon={<StoreIcon />} color="primary" label={storeName.toUpperCase()} sx={{ fontWeight: 700, fontSize: '1rem', py: 2.2, px: 1 }} />
          </Tooltip>
        )}
      </Paper>

      <Tabs value={tab} onChange={(_, v) => go({ tab: v, run: v === 'workflow' ? runId : undefined })} sx={{ mb: 2 }}>
        <Tab value="workflow" label="Workflow" />
        <Tab value="purchase-orders" label="Purchase Orders" />
        <Tab value="history" label="History (90 days)" />
      </Tabs>

      {tab === 'workflow' && (runId
        ? <RunWorkspace key={runId} runId={runId} onBack={() => go({ run: null })} />
        : storeId
          ? <DataStep key={storeId} storeId={storeId} storeName={storeName} onOpenRun={openRun} />
          : <Alert severity="info">Select a store with the store button at the top of the screen to upload reports and start a run.</Alert>)}
      {tab === 'purchase-orders' && <PurchaseOrders storeId={storeId} />}
      {tab === 'history' && <History storeId={storeId} onOpenRun={openRun} />}
    </Box>
  );
};

export default OrderProcessor;
